import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Toggle } from '../../Objects/Toggle/Toggle'
import uuidv4 from '../../utils/uuidv4'

/**
 * Bronson ShowMore component.
 * Show More is an accessible content-reveal component. It consists of a content body and a button that is displayed below.
 * The internal state is handled via the {@link Toggle} object.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-show-more.html
 *
 * @param {React.ReactNode|React.ReactNodeArray} [children] - Any content to be expanded/collapsed.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {boolean} [defaultExpanded=false] - Set the initial state to be expanded or collapsed.
 * @param {string} [labelCollapse] - The label to display in collapsed state.
 * @param {string} [labelExpand] - The label to display in expanded state.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The ShowMore component.
 * @constructor
 */
export function ShowMore({
  children,
  className,
  defaultExpanded = false,
  labelCollapse,
  labelExpand,
  testId,
  ...otherProps
}) {
  const ref = useRef(null)
  const classNameList = classNames('c-show-more', className).trim()
  const buttonId = uuidv4()
  const targetId = uuidv4()

  return (
    <div className={classNameList}>
      <div className="c-show-more__body js-toggle-target" id={targetId} data-testid={testId} ref={ref} {...otherProps}>
        {/* Allow HTML content to be passed in. */}
        {children}
      </div>
      {/* Use {@link Toggle} object here. */}
      <Toggle
        className="c-show-more__button"
        iconClassName="c-show-more__icon"
        id={buttonId}
        targetId={targetId}
        /**
         * The {@link Toggle} object takes the invert state.
         */
        defaultHidden={!defaultExpanded}
        labelHidden={labelExpand}
        labelUnhidden={labelCollapse}
        targetRef={ref}
        /**
         * Pass in {@link Button.propTypes}.
         */
        icon
        iconViaCss
        link
        round
        secondary
        small
      />
    </div>
  )
}

ShowMore.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.array]),
  className: PropTypes.string, // Additional CSS classes on the component element.
  labelCollapse: PropTypes.string, // Bronson template: 'label-collapse'.
  labelExpand: PropTypes.string, // Bronson template: 'label-expand'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  /* Convenience props. */
  defaultExpanded: PropTypes.bool, // Convenience prop for `[data-toggle-hidden="false"]`.
}
